import React, { useState } from "react";
import { Ff, GraphqlOperations } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { useToast } from "@src/lib/hooks/useToast";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";
import {
  useOrgFeatureFlagsQuery,
  useUpdateOrgFeatureFlagsMutation,
} from "./SetOrgFeatureFlags.generated";

/**
 * Flags the October 2026 release turns on for everyone on launch day (Notion
 * "Launch checklist", section 1). OpusAgentAttachments and OpusAgentVoice are
 * already on globally, and MCP is ungated.
 */
export const OCTOBER_RELEASE_FLAGS: { flag: Ff; label: string }[] = [
  { flag: Ff.OpusAgent, label: "Opus Agents" },
  { flag: Ff.OpusAgentWrites, label: "Opus Agents drafting" },
  {
    flag: Ff.LibraryItemOverviewTab,
    label: "Content Overview and Video Editor",
  },
  { flag: Ff.PathsAsWorkflows, label: "Path builder" },
  { flag: Ff.AuditScoringThresholds, label: "Grades" },
  { flag: Ff.ProfileImage, label: "Profile photos" },
];

const OptInToOctoberRelease: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { addToast, addErrorToast } = useToast();
  const { data } = useOrgFeatureFlagsQuery();
  const enabledFlags = new Set(
    data?.OrgFeatureFlags.filter((f) => f.enabled).map((f) => f.name),
  );
  const allEnabled =
    !!data && OCTOBER_RELEASE_FLAGS.every((f) => enabledFlags.has(f.flag));
  const [updateFlags, { loading }] = useUpdateOrgFeatureFlagsMutation({
    refetchQueries: [
      GraphqlOperations.Query.OrgFeatureFlags,
      GraphqlOperations.Query.FeatureFlags,
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (!data.updateOrgFeatureFlags.success) {
        addErrorToast({ ...data, callsite: "opt_in_to_october_release" });
        return;
      }
      setOpen(false);
      addToast({ message: "October release turned on for this org" });
    },
    onError: (e) => addToast({ message: e.message }),
  });

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={allEnabled}
        onClick={() => setOpen(true)}
      >
        {allEnabled
          ? "Opted in to October release"
          : "Opt in to October release for this Org"}
      </Button>
      {open && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Opt in to October release</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2">
              <Text variant="compact-description" multiline>
                Turns on these org feature flags:
              </Text>
              <ul className="list-disc pl-5">
                {OCTOBER_RELEASE_FLAGS.map(({ flag, label }) => (
                  <li key={flag}>
                    <Text variant="compact-description" multiline>
                      {label} ({flag})
                      {enabledFlags.has(flag) ? " · already on" : ""}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
            <AlertDialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={() =>
                  updateFlags({
                    variables: {
                      input: {
                        featureFlags: OCTOBER_RELEASE_FLAGS.map(({ flag }) => ({
                          name: flag,
                          enabled: { value: true },
                        })),
                      },
                    },
                  })
                }
              >
                Turn on
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export default OptInToOctoberRelease;
